import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Checkbox } from '@mui/material';
import { StagedVideo } from '../common/model/type/video-file.types';
import { RecordColumn } from '../common/model/ui/record-list.type';
import OvCompactButton from '../components/UI/atoms/OvCompactButton';
import OvTextField from '../components/UI/atoms/OvTextField';
import OvStagedVideoCell from '../components/UI/molecules/OvStagedVideoCell';
import OvVideoHealthGoalsField from '../components/UI/molecules/OvVideoHealthGoalsField';
import OvVideoPlacementPicker from '../components/UI/molecules/OvVideoPlacementPicker';
import OvVideoStateNotice from '../components/UI/molecules/OvVideoStateNotice';
import { isPlacementPositionValid } from '../common/utils/video-placement-utils';
import {
  isStagedPlaced,
  stagedFieldId,
  stagedFieldsId,
} from '../common/utils/video-staging-utils';
import Colours from '../design-system/colours';
import Variables from '../design-system/variables';
import { useVideoStagingSession } from './use-video-staging-session';

interface StagedVideoColumnsOptions {
  selectedKeys: string[];
  onToggleSelected: (key: string) => void;
}

export function useStagedVideoColumns({
  selectedKeys,
  onToggleSelected,
}: StagedVideoColumnsOptions): RecordColumn<StagedVideo>[] {
  const { t } = useTranslation();
  const session = useVideoStagingSession();
  const isDisabled = session.isSubmitting;
  const needsPlacement = <Muted>{t('videos.bulk.needsPlacement')}</Muted>;

  return [
    {
      id: 'select',
      header: t('videos.bulk.columns.select'),
      isHeaderHidden: true,
      cardRole: 'heading',
      renderCell: ({ key, file }) => (
        <Checkbox
          size="small"
          checked={selectedKeys.includes(key)}
          disabled={isDisabled}
          inputProps={{
            'aria-label': t('videos.bulk.selectFile', { name: file.name }),
          }}
          onChange={() => onToggleSelected(key)}
        />
      ),
    },
    {
      id: 'video',
      header: t('videos.fields.video'),
      cardRole: 'heading',
      renderCell: (item) => <OvStagedVideoCell item={item} />,
    },
    {
      id: 'shownIn',
      header: t('videos.fields.shownIn'),
      renderCell: (item) => (
        <Stack>
          <OvVideoPlacementPicker
            id={`${stagedFieldsId(item.key)}-placement`}
            label={t('videos.fields.shownIn')}
            value={item.placement.placement_key}
            isDisabled={isDisabled}
            onChange={(placement_key) =>
              session.setPlacement(item.key, { placement_key })
            }
          />
          {isStagedPlaced(item) && (
            <OvVideoStateNotice
              notice={{ kind: 'stagedAppears' }}
              variant="compact"
            />
          )}
        </Stack>
      ),
    },
    {
      id: 'order',
      header: t('videos.fields.order'),
      renderCell: (item) => {
        if (!isStagedPlaced(item)) {
          return needsPlacement;
        }

        const isInvalid = !isPlacementPositionValid(item.placement);

        return (
          <OvTextField
            id={stagedFieldId(item.key, 'position')}
            size="small"
            label={t('videos.fields.order')}
            value={item.placement.position}
            disabled={isDisabled}
            inputProps={{ inputMode: 'numeric' }}
            error={isInvalid}
            helperText={
              isInvalid ? t('videos.placement.positionInvalid') : undefined
            }
            onChange={(event) =>
              session.setPlacement(item.key, { position: event.target.value })
            }
          />
        );
      },
    },
    {
      id: 'healthGoals',
      header: t('videos.fields.healthGoals'),
      renderCell: (item) =>
        isStagedPlaced(item) ? (
          <OvVideoHealthGoalsField
            goals={item.placement.health_goals}
            isDisabled={isDisabled}
            onChange={(health_goals) =>
              session.setPlacement(item.key, { health_goals })
            }
          />
        ) : (
          needsPlacement
        ),
    },
    {
      id: 'actions',
      header: t('videos.bulk.columns.actions'),
      isHeaderHidden: true,
      cardRole: 'footer',
      renderCell: ({ key, file }) => (
        <OvCompactButton
          tone="secondary"
          aria-label={t('videos.staging.removeFile', { name: file.name })}
          disabled={isDisabled}
          onClick={() => session.remove(key)}
        >
          {t('videos.staging.remove')}
        </OvCompactButton>
      ),
    },
  ];
}

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 14rem;
`;

const Muted = styled.span`
  font-size: ${Variables.fontSizes.SMALL};
  color: ${Colours.OV_CHARCOAL};
`;
